import { Link } from '@tanstack/react-router';

import type { GapDraftView } from '@momentum/types';

import type { GapReviewData } from '../gap-review-context';
import { pluralize } from '../lib/format';
import {
  countByStatus,
  groupByScope,
  QUEUE_FILTERS,
  type QueueFilter,
} from '../lib/queue';
import { PaneResizer } from './PaneResizer';
import { QueueRow } from './QueueRow';
import { RunPicker } from './RunPicker';
import { RunSummaryCard } from './RunSummaryCard';

const FILTER_LABELS: Record<QueueFilter, string> = {
  todo: 'To review',
  keep: 'Kept',
  drop: 'Dropped',
  all: 'All',
};

const FILTER_TONES: Record<QueueFilter, string> = {
  todo: '',
  keep: 'emerald',
  drop: 'rose',
  all: '',
};

interface QueueSidebarProps {
  gapReviewData: GapReviewData;
  /** The run's drafts matching the verdict filter, already ordered. */
  visibleDrafts: GapDraftView[];
  filter: QueueFilter;
  currentDraftId: string | undefined;
  onExecutionChange: (executionId: string) => void;
  onFilterChange: (filter: QueueFilter) => void;
  onSelectDraft: (draftId: string) => void;
  onResize: (width: number) => void;
  onResetWidth: () => void;
}

/**
 * The queue: a run picker, a verdict filter, overall progress, and the rows
 * grouped by scope.
 *
 * Every count here is over the selected run — which is every draft loaded — but
 * NOT over the verdict filter. Those are two different questions: "Kept 4"
 * beside a seven-gap run means four of those seven, while a chip that recounted
 * itself once pressed would say 4 and then, listing exactly those four, say
 * something else.
 */
export function QueueSidebar({
  gapReviewData,
  visibleDrafts,
  filter,
  currentDraftId,
  onExecutionChange,
  onFilterChange,
  onSelectDraft,
  onResize,
  onResetWidth,
}: QueueSidebarProps) {
  const { drafts, executions, executionId } = gapReviewData;
  const counts = countByStatus(drafts);
  const reviewedCount = counts.kept + counts.dropped;
  const scopeGroups = groupByScope(visibleDrafts, drafts);

  const filterCounts: Record<QueueFilter, number> = {
    todo: counts.pending,
    keep: counts.kept,
    drop: counts.dropped,
    all: drafts.length,
  };

  // A progress bar over zero drafts would divide by zero; an empty run simply
  // has no progress to draw.
  const percentOf = (count: number) =>
    drafts.length === 0 ? 0 : (count / drafts.length) * 100;

  return (
    <aside className="gr-queue">
      <div className="gr-queue-head">
        <RunPicker
          executions={executions}
          value={executionId}
          onChange={onExecutionChange}
        />

        <RunSummaryCard executionId={executionId} />

        {/* The count is its own element so the active tab can put it in a pill:
            the filter decides what the whole list below means, and it has to be
            answerable at a glance rather than by comparing four near-identical
            chips. */}
        <div className="gr-filters">
          {QUEUE_FILTERS.map((queueFilter) => (
            <button
              key={queueFilter}
              className="gr-filter"
              type="button"
              data-tone={FILTER_TONES[queueFilter]}
              aria-pressed={filter === queueFilter}
              onClick={() => onFilterChange(queueFilter)}
            >
              {FILTER_LABELS[queueFilter]}
              <i>{filterCounts[queueFilter]}</i>
            </button>
          ))}
        </div>

        <div>
          <div className="gr-progress-row">
            <span>Progress</span>
            <span>
              {reviewedCount} of {drafts.length} reviewed
            </span>
          </div>
          <div className="gr-progress-track">
            <i
              className="gr-progress-keep"
              style={{ width: `${percentOf(counts.kept)}%` }}
            />
            <i
              className="gr-progress-drop"
              style={{ width: `${percentOf(counts.dropped)}%` }}
            />
          </div>
        </div>

        {filter === 'keep' && (
          <p className="gr-queue-hint">
            This chip filters the queue. The second pass over kept gaps — the
            readiness gate before anything reaches the <code>gaps</code>{' '}
            collection — lives in{' '}
            <Link className="gr-linkbtn" to="/gap-review/final">
              Final review →
            </Link>
          </p>
        )}
      </div>

      <div className="gr-queue-list">
        {visibleDrafts.length === 0 ? (
          <div className="gr-empty gr-empty-inset">
            <b>Nothing here</b>No draft matches this filter.
          </div>
        ) : (
          scopeGroups.map((scopeGroup) => (
            <div key={scopeGroup.scopeExternalId}>
              <div className="gr-group-head">
                {scopeGroup.scopeName} ·{' '}
                {pluralize(scopeGroup.draftCount, 'gap')}
              </div>
              {scopeGroup.drafts.map((draft) => (
                <QueueRow
                  key={draft._id}
                  draft={draft}
                  isCurrent={draft._id === currentDraftId}
                  onSelect={onSelectDraft}
                />
              ))}
            </div>
          ))
        )}
      </div>

      <PaneResizer
        edge="end"
        label="Resize the queue"
        onResize={onResize}
        onReset={onResetWidth}
      />
    </aside>
  );
}
